import { AiImageGenerationCost, AiImageStudioConfig, AiImageStudioDraft, GptImage25Pricing } from './types';
import { getOpenAiImageSize } from './openaiImageSize';

const FALLBACK_USD_SEK_RATE = 11;
const BASE_PIXELS = 1024 * 1024;

const GEMINI_OUTPUT_USD: Record<string, Record<string, number>> = {
  'gemini-3.1-flash-image': {
    '1K': 0.067,
    '2K': 0.101,
    '4K': 0.151,
  },
  'gemini-3-pro-image': {
    '1K': 0.134,
    '2K': 0.134,
    '4K': 0.24,
  },
};

const GEMINI_REFERENCE_INPUT_USD: Record<string, number> = {
  'gemini-3.1-flash-image': 0.0003,
  'gemini-3-pro-image': 0.0011,
};

const FALLBACK_GPT_IMAGE_25: GptImage25Pricing = {
  imageInputPer1M: 8,
  cachedImageInputPer1M: 2,
  imageOutputPer1M: 30,
  textInputPer1M: 5,
  cachedTextInputPer1M: 1.25,
  outputTokens: {
    square1024: {
      low: 196,
      medium: 439,
      high: 1756,
      xhigh: 3122,
      max: 7024,
    },
    portrait1024x1536: {
      low: 158,
      medium: 343,
      high: 1372,
      xhigh: 2459,
      max: 5488,
    },
  },
};

type GptImageQuality = keyof GptImage25Pricing['outputTokens']['square1024'];

const GPT_IMAGE_QUALITIES: GptImageQuality[] = ['low', 'medium', 'high', 'xhigh', 'max'];

function getUsdSekRate(config: AiImageStudioConfig | null): number {
  const rate = Number(config?.pricing?.usdSekRate);
  return Number.isFinite(rate) && rate > 0 ? rate : FALLBACK_USD_SEK_RATE;
}

function isGptImageQuality(value: string): value is GptImageQuality {
  return GPT_IMAGE_QUALITIES.includes(value as GptImageQuality);
}

function resolveGptImage25Pricing(config: AiImageStudioConfig | null): GptImage25Pricing {
  const pricing = config?.pricing?.gptImage25;
  const square = pricing?.outputTokens?.square1024;
  const portrait = pricing?.outputTokens?.portrait1024x1536;
  if (
    pricing
    && square
    && portrait
    && typeof pricing.imageOutputPer1M === 'number'
    && typeof pricing.textInputPer1M === 'number'
    && GPT_IMAGE_QUALITIES.every((quality) => typeof square[quality] === 'number' && typeof portrait[quality] === 'number')
  ) {
    return pricing;
  }
  return FALLBACK_GPT_IMAGE_25;
}

function outputTokenEstimate(
  width: number,
  height: number,
  quality: GptImageQuality,
  pricing: GptImage25Pricing,
): { tokens: number; areaScaled: boolean } {
  if (width === 1024 && height === 1024) {
    return { tokens: pricing.outputTokens.square1024[quality], areaScaled: false };
  }
  if ((width === 1024 && height === 1536) || (width === 1536 && height === 1024)) {
    return { tokens: pricing.outputTokens.portrait1024x1536[quality], areaScaled: false };
  }
  return {
    tokens: pricing.outputTokens.square1024[quality] * (width * height) / BASE_PIXELS,
    areaScaled: true,
  };
}

function formatSek(value: number): string {
  return new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: 'SEK',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatRate(value: number): string {
  return new Intl.NumberFormat('sv-SE', { maximumFractionDigits: 4 }).format(value);
}

function referenceNote(draft: AiImageStudioDraft): string | null {
  if (draft.editMode) {
    return 'Basbild och referensbilder ingår inte i uppskattningen och syns i den faktiska kostnaden.';
  }
  if (draft.references.length > 0) {
    return 'Referensbilder ingår inte i uppskattningen och syns i den faktiska kostnaden.';
  }
  return null;
}

function buildOpenAiCostText(
  minPerImageSek: number,
  maxPerImageSek: number,
  count: number,
  areaScaled: boolean,
  note: string | null,
): string {
  const range = Math.abs(maxPerImageSek - minPerImageSek) > 0.0001;
  const perImage = range
    ? `${formatSek(minPerImageSek)}-${formatSek(maxPerImageSek)}/bild`
    : `${formatSek(minPerImageSek)}/bild`;
  const total = range
    ? `${formatSek(minPerImageSek * count)}-${formatSek(maxPerImageSek * count)}`
    : formatSek(minPerImageSek * count);
  const parts = [`Ca ${perImage}`];
  if (areaScaled) parts.push('ungefärlig för denna storlek');
  parts.push(`totalt ca ${total}`);
  const text = parts.join(' · ');
  return note ? `${text}. ${note}` : text;
}

export function estimateAiImageCostText(config: AiImageStudioConfig | null, draft: AiImageStudioDraft): string | null {
  const model = draft.model || config?.defaultModel || '';
  const count = Math.max(1, Number(draft.count) || 1);
  const usdSekRate = getUsdSekRate(config);

  if (draft.provider === 'openai') {
    const [width, height] = getOpenAiImageSize(draft.aspectRatio, draft.imageSize).split('x').map(Number);
    const safeWidth = Number.isFinite(width) && width > 0 ? width : 1024;
    const safeHeight = Number.isFinite(height) && height > 0 ? height : 1024;
    const pricing = resolveGptImage25Pricing(config);
    const quality = draft.quality || 'auto';
    const qualities: [GptImageQuality, GptImageQuality] = quality === 'auto' || !isGptImageQuality(quality)
      ? ['low', 'max']
      : [quality, quality];
    const promptTokens = Math.ceil(Math.max(0, draft.prompt.length) / 4);
    const textUsd = (promptTokens / 1_000_000) * pricing.textInputPer1M;
    const low = outputTokenEstimate(safeWidth, safeHeight, qualities[0], pricing);
    const high = outputTokenEstimate(safeWidth, safeHeight, qualities[1], pricing);
    const minPerImageSek = (((low.tokens / 1_000_000) * pricing.imageOutputPer1M) + textUsd) * usdSekRate;
    const maxPerImageSek = (((high.tokens / 1_000_000) * pricing.imageOutputPer1M) + textUsd) * usdSekRate;
    return buildOpenAiCostText(
      minPerImageSek,
      maxPerImageSek,
      count,
      low.areaScaled || high.areaScaled,
      referenceNote(draft),
    );
  }

  const modelPricing = GEMINI_OUTPUT_USD[model];
  if (!modelPricing) return null;

  const outputUsd = modelPricing[draft.imageSize] || modelPricing['1K'];
  const referenceInputUsd = draft.references.length * (GEMINI_REFERENCE_INPUT_USD[model] || 0);
  const perImageUsd = outputUsd + referenceInputUsd;
  return `Ca ${formatSek(perImageUsd * usdSekRate)}/bild · totalt ca ${formatSek(perImageUsd * count * usdSekRate)}`;
}

export function describeActualImageCost(
  cost: AiImageGenerationCost | undefined | null,
  usdSekRate?: number,
): { summary: string; lines: Array<{ label: string; value: string }> } | null {
  if (!cost) return null;
  if (cost.accuracy === 'unavailable' || typeof cost.sek !== 'number') {
    return {
      summary: 'API-kostnaden kunde inte läsas från svaret.',
      lines: [],
    };
  }

  const amount = formatSek(cost.sek);
  if (cost.accuracy === 'partial-usage') {
    return {
      summary: `Ungefärlig från ofullständig usage: ${amount}`,
      lines: [],
    };
  }

  const rate = typeof usdSekRate === 'number' && Number.isFinite(usdSekRate) && usdSekRate > 0
    ? usdSekRate
    : undefined;
  const lines: Array<{ label: string; value: string }> = [];
  if (cost.breakdown && rate) {
    const toSek = (usd: number) => formatSek(usd * rate);
    lines.push({ label: 'Bildoutput', value: toSek(cost.breakdown.imageOutputUsd) });
    lines.push({
      label: 'Bildinput',
      value: toSek(cost.breakdown.imageInputUsd + cost.breakdown.cachedImageInputUsd),
    });
    lines.push({
      label: 'Textinput',
      value: toSek(cost.breakdown.textInputUsd + cost.breakdown.cachedTextInputUsd),
    });
    lines.push({ label: 'Växelkurs', value: `${formatRate(rate)} kr/USD` });
  }

  return {
    summary: `Faktisk API-kostnad: ${amount}`,
    lines,
  };
}
